import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  FrontendModule,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import {
  azureSiteApiRef,
  AzureSitesApiBackendClient,
} from '@backstage-community/plugin-azure-sites';

/**
 * APIs contributed by the app itself.
 *
 * In the new frontend system an API may only be provided by a module for the
 * plugin that owns its ref, which is derived from the ref id: `core.*` is owned
 * by `app`, `plugin.<id>.*` by that plugin, and any other prefix by a plugin of
 * that name. Only a *conflict* — two different plugins providing the same ref —
 * is rejected, so an owner that is not installed can be stood in for from here.
 *
 * `integration.scmintegrations` nominally belongs to a plugin called
 * `integration`, and `plugin.azure-sites.service` to one called `azure-sites`.
 * Neither exists in this app: `@backstage-community/plugin-azure-sites` has no
 * new frontend system entrypoint, so its entity content and API are wired up by
 * hand (see `components/catalog/extensions.tsx`). A module targeting an absent
 * plugin is silently dropped, so both are registered here instead.
 */
const appApis = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'scm-integrations',
      params: defineParams =>
        defineParams({
          api: scmIntegrationsApiRef,
          deps: { configApi: configApiRef },
          factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
        }),
    }),
    ApiBlueprint.make({
      name: 'scm-auth',
      params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
    }),
    ApiBlueprint.make({
      name: 'azure-sites',
      params: defineParams =>
        defineParams({
          api: azureSiteApiRef,
          deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
          factory: ({ discoveryApi, identityApi }) =>
            new AzureSitesApiBackendClient({ discoveryApi, identityApi }),
        }),
    }),
  ],
});

export const apiModules: FrontendModule[] = [appApis];
